/**
 * "Why am I waiting?": what the person sees while Flow or their sandbox makes them wait, instead of a bare spinner
 * and requests that hang (hooks/useSandboxWake.js, the sandbox gateway's `GET /api/v1/sandbox/state`, turned into one
 * answer by utils/waitingState.js). Browser-side problems (offline, slow, Flow unreachable) are told apart from
 * server-side waits (Flow being updated, the sandbox waking, a start refused). While the sandbox wakes: the step in
 * plain words, the time it has taken against the usual, a progress bar, Retry once it takes longer than usual. When
 * nothing is needed from the person it says so; otherwise the next step and its button (e.g. "Change plan").
 *
 * - <SandboxWaking fallback={…} watch />: the card while there is a wait (the sandbox wakes, failed to start, Flow is
 *   updating, the connection is down…), else `fallback` (the page's own loading state). `compact`: no progress bar,
 *   for a page that already shows content.
 * - <SandboxWakeBanner />: app-wide (App.jsx), the sandbox waking or the connection down/slow/unreachable, when no
 *   page shows the card itself.
 * - <FlowUpdatingBanner />: app-wide (App.jsx), "Flow is being updated — back in about N minutes" (the state answer's
 *   `flow.updating`, or 502/503/504 answers), or "Flow was updated" with Reload (a new version detected,
 *   utils/staleBuild.js), when no page shows the card itself.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'antd';
import useSandboxWake, { inlineShown } from '../../hooks/useSandboxWake';
import { LEGACY_PHASE_TEXT, formatElapsed as formatSeconds } from '../../utils/waitingState';
import { PlanChangeButton } from './PlanRequest';
import './SandboxWaking.css';

// A typical wake of an older gateway (no estimate): 20-60 s for a resume, a first start longer; the DNS wait up to 35 s.
const TYPICAL_MS = 180_000;
export { SLOW_AFTER_MS } from '../../utils/waitingState';

/** Older gateways: the phase's line and a percentage that moves with time inside the phase's range. */
export function wakeProgress(status, elapsedMs) {
  const phase = LEGACY_PHASE_TEXT[status?.wakePhase || status?.phase] || LEGACY_PHASE_TEXT.checking;
  const span = Math.min(1, Math.max(0, elapsedMs / TYPICAL_MS));
  return { text: phase.text, percent: Math.round(phase.from + ((phase.to - phase.from) * span)) };
}

export const formatElapsed = (ms) => formatSeconds(ms / 1000);

const ALERT_KINDS = new Set(['blocked', 'offline', 'flow_updated']);

function WaitingCard({ wake, compact = false, banner = false }) {
  const { waiting, retry, retrying, retryError } = wake;
  const { kind } = waiting;
  const className = [
    'sbx-waking', `sbx-waiting-${kind.replace('_', '-')}`, compact ? 'compact' : '', banner ? 'banner' : '',
    kind === 'blocked' ? 'failed' : '',
  ].filter(Boolean).join(' ');
  let action = null;
  if (waiting.action === 'plan') {
    action = <PlanChangeButton type="primary" />;
  } else if (waiting.action === 'retry') {
    action = <Button loading={retrying} onClick={retry} size="small" type={kind === 'blocked' ? 'primary' : 'default'}>Retry</Button>;
  } else if (waiting.action === 'reload') {
    action = <Button onClick={() => window.location.reload()} size="small" type="primary">Reload</Button>;
  }
  const alert = ALERT_KINDS.has(kind);
  return (
    <div aria-live={alert ? undefined : 'polite'} className={className} data-side={waiting.side} role={alert ? 'alert' : 'status'}>
      <div className="sbx-waking-text">
        <strong>{waiting.title}</strong>
        <span>{waiting.detail}</span>
        {waiting.note && <span className="sbx-waking-note">{waiting.note}</span>}
        {kind === 'waking' && !compact && (
          <div aria-label="Waking progress" aria-valuemax={100} aria-valuemin={0} aria-valuenow={waiting.percent} className="sbx-waking-bar" role="progressbar">
            <span style={{ width: `${waiting.percent}%` }} />
          </div>
        )}
        {waiting.youNeedToAct && waiting.nextStep && <span className="sbx-waking-next">{waiting.nextStep}</span>}
        {waiting.hint && <span className="sbx-waking-note sbx-waking-hint">{waiting.hint}</span>}
        {kind === 'waking' && !compact && !banner && waiting.expectedSeconds === null && (
          <span className="sbx-waking-note">It slept after a while without use. Your projects open as soon as it is up.</span>
        )}
        {retryError && <span className="sbx-waking-error" role="alert">{retryError}</span>}
      </div>
      {action && <div className="sbx-waking-actions">{action}</div>}
    </div>
  );
}

WaitingCard.propTypes = { wake: PropTypes.object.isRequired, compact: PropTypes.bool, banner: PropTypes.bool };

export default function SandboxWaking({ fallback = null, watch = false, compact = false }) {
  const wake = useSandboxWake({ watch, inline: true });
  if (!wake.waiting) { return fallback; }
  return <WaitingCard compact={compact} wake={wake} />;
}

SandboxWaking.propTypes = { fallback: PropTypes.node, watch: PropTypes.bool, compact: PropTypes.bool };

// The sandbox and the person's connection; Flow's own update has its banner (FlowUpdatingBanner).
const WAKE_BANNER_KINDS = new Set(['waking', 'offline', 'unreachable', 'slow']);
const FLOW_BANNER_KINDS = new Set(['flow_updating', 'flow_updated']);

export function SandboxWakeBanner() {
  const wake = useSandboxWake();
  if (!wake.waiting || !WAKE_BANNER_KINDS.has(wake.waiting.kind) || inlineShown()) { return null; }
  return <WaitingCard banner compact wake={wake} />;
}

export function FlowUpdatingBanner() {
  const wake = useSandboxWake({ flowBanner: true });
  if (!wake.waiting || !FLOW_BANNER_KINDS.has(wake.waiting.kind) || inlineShown()) { return null; }
  return <WaitingCard banner compact wake={wake} />;
}
